import { useQuery } from '@tanstack/react-query';
import { shop, provider, staff } from '@/shared/api/shop';
import {
  USER_DATA_OPTIONS,
  VALIDATION_DATA_OPTIONS,
} from '@/shared/config/queryOptions';
import { shopKeys } from '../core/keys';

// Provider queries
export const useProviders = (shopId: string) => {
  return useQuery({
    queryKey: shopKeys.providers({ shopId }),
    queryFn: () => provider.getProviders(shopId),
    enabled: !!shopId,
    ...USER_DATA_OPTIONS,
  });
};

export const useProvider = (providerId: string) => {
  return useQuery({
    queryKey: shopKeys.provider(providerId),
    queryFn: () => provider.getProvider(providerId),
    enabled: !!providerId,
    ...USER_DATA_OPTIONS,
  });
};

export const useShopsByProvider = (providerId: string | undefined) => {
  return useQuery({
    queryKey: shopKeys.providerShops(providerId),
    queryFn: async () => {
      if (!providerId) {
        return [];
      }
      return await shop.fetchShopsByProviderId(providerId);
    },
    enabled: !!providerId,
    ...USER_DATA_OPTIONS,
  });
};

// Staff membership queries
export const useStaffMemberships = (
  shopId: string,
  filters?: {
    isActive?: boolean;
    contractType?: string;
    startDate?: string;
    endDate?: string;
    include?: string[];
  },
) => {
  return useQuery({
    queryKey: shopKeys.staffMembers(shopId, filters),
    queryFn: async () => {
      if (!shopId) {
        return [];
      }
      return await staff.getShopStaff(shopId, filters);
    },
    enabled: !!shopId,
    ...USER_DATA_OPTIONS,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const useProviderMemberships = (
  providerId: string,
  isActive?: boolean,
) => {
  return useQuery({
    queryKey: shopKeys.providerMemberships(providerId, { isActive }),
    queryFn: () =>
      staff.getProviderMemberships(providerId, {
        isActive,
        include: ['provider', 'shop'],
      }),
    enabled: !!providerId,
    ...USER_DATA_OPTIONS,
  });
};

export const useMembership = (membershipId: string) => {
  return useQuery({
    queryKey: shopKeys.membership(membershipId),
    queryFn: () => staff.getMembership(membershipId),
    enabled: !!membershipId,
    ...USER_DATA_OPTIONS,
  });
};

// Invitations
export const usePendingInvitations = (shopId: string) => {
  return useQuery({
    queryKey: shopKeys.invitations(shopId),
    queryFn: async () => {
      if (!shopId) {
        return [];
      }
      return await staff.getPendingInvitations(shopId);
    },
    enabled: !!shopId,
    ...USER_DATA_OPTIONS,
  });
};

export const useInvitation = (code: string) => {
  return useQuery({
    queryKey: shopKeys.invitation(code),
    queryFn: () => staff.getInvitation(code),
    enabled: !!code,
    ...USER_DATA_OPTIONS,
  });
};

export const useInvitationValidation = (code: string) => {
  return useQuery({
    queryKey: [...shopKeys.invitation(code), 'validation'],
    queryFn: () => staff.validateInvitation(code),
    enabled: !!code,
    ...VALIDATION_DATA_OPTIONS,
  });
};

// Booth Rental Tier queries
export const useBoothRentalTiers = (
  membershipId: string,
  isActive?: boolean,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: shopKeys.rentalTiers(membershipId),
    queryFn: () => staff.getMembershipRentalTiers(membershipId, isActive),
    // Callers can pass `enabled: false` to avoid fetching tiers in contexts
    // where they are irrelevant (e.g. COMMISSION staff in the edit modal) —
    // preventing a wasted request and, when the route is gated server-side,
    // a spurious 404.
    enabled: !!membershipId && (options?.enabled ?? true),
    ...USER_DATA_OPTIONS,
  });
};
